import React, { FunctionComponent, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import Moment from 'react-moment'
import { Link, useNavigate } from 'react-router-dom'
import { useLocalStorageState } from '../../hooks/LocalStorageState'
import AuthRole from '../../role'
import { HostData, Product } from '../Customer/Data'
import Toaster from '../Toaster'
import { CustomerSummariesStatus, CustomerSummary, RiskLevel } from './CustomerSummaries'
import { lifecycleRank, lifecycleStageFor, stageOrDefault } from './lifecycle'
import { formatMoney } from './briefHelpers'
import styles from './CustomerIntelligenceTable.module.css'

interface Props {
  teamID: string
  hosts: HostData[]
  summaries?: Map<string, CustomerSummary>
  summariesStatus?: CustomerSummariesStatus
  searchFilter?: string
  onSearchChange?: (value: string) => void
  searchTerm?: string
  onUpdated?: () => void
}

interface Column {
  label: string
  numeric?: boolean
  sortValue?: (host: HostData, summary?: CustomerSummary) => SortValue
  defaultDirection?: SortDirection
}

type SortValue = string | number | undefined
type SortDirection = 'ascending' | 'descending'
interface SortKey {
  label: string
  direction: SortDirection
}

const EMPTY_VALUE = '—'
const NOT_APPLICABLE = 'Not applicable'
const NO_DATA_YET = 'No data yet'

const isHealthCheckNotApplicable = (host: HostData): boolean => {
  const product = host.product?.trim().toLowerCase()
  return product === Product.Hive || product === Product.Pocket
}

const isStale = (generatedAt: string): boolean => moment(generatedAt).isBefore(moment().subtract(1, 'month'))

type BriefAction = 'update' | 'generate'

const briefActions: Record<BriefAction, { path: string; label: string; done: string; tip: [string, string] }> = {
  update: {
    path: 'rebuild',
    label: 'Update',
    done: 'updated',
    tip: ['Brief is out of date.', 'Update to get the latest.'],
  },
  generate: { path: 'generate', label: 'Generate', done: 'generated', tip: ['No brief yet.', 'Generate one now.'] },
}

const formatCount = (n: number): string => Math.round(n).toLocaleString('en-US')

const formatTrend = (fraction: number): React.ReactNode => {
  const magnitude = `${(Math.abs(fraction) * 100).toFixed(1).replace(/\.0$/, '')}%`
  if (fraction > 0) {
    return (
      <>
        <span className={styles.trendUp}>↑</span> {magnitude}
      </>
    )
  }
  if (fraction < 0) {
    return (
      <>
        <span className={styles.trendDown}>↓</span> {magnitude}
      </>
    )
  }
  return magnitude
}

const formatFleetValue = (currencyCode?: string) => (value: number): string => formatMoney(value, currencyCode)

const formatOrEmpty = <T,>(value: T | undefined, format: (value: T) => React.ReactNode): React.ReactNode =>
  value == null ? EMPTY_VALUE : format(value)

const riskClassByLevel: Record<RiskLevel, string | undefined> = {
  Unknown: undefined,
  Low: styles.riskLow,
  Medium: styles.riskMedium,
  High: styles.riskHigh,
  'Very High': styles.riskHigh,
  Extreme: styles.riskHigh,
}

// Ordered lowest to highest, so a level's index doubles as its sort rank.
const RISK_LEVELS = Object.keys(riskClassByLevel) as RiskLevel[]

const toRiskLevel = (risk: string): RiskLevel | undefined =>
  RISK_LEVELS.find(candidate => candidate.toLowerCase() === risk.trim().toLowerCase())

const riskClassName = (risk: string): string | undefined => {
  const level = toRiskLevel(risk)
  return level ? riskClassByLevel[level] : undefined
}

const formatRisk = (risk: string): React.ReactNode => <span className={riskClassName(risk)}>{risk}</span>

/* the stage's name beside a dot in its Briefs-tab colour */
const formatLifecycleStage = (value: string): React.ReactNode => {
  const stage = lifecycleStageFor(value)
  if (!stage) return EMPTY_VALUE
  return (
    <span className={styles.lifecycleStage} data-stage={stage.value}>
      {stage.label}
    </span>
  )
}

// Unknown risk sorts with missing values, below every known level.
const riskRank = (risk?: string): number | undefined => {
  const level = risk ? toRiskLevel(risk) : undefined
  return level && level !== 'Unknown' ? RISK_LEVELS.indexOf(level) : undefined
}

const columns: Column[] = [
  { label: 'Customer', sortValue: host => host.name },
  {
    label: 'Lifecycle stage',
    sortValue: (_, summary) => lifecycleRank(stageOrDefault(summary?.lifecycleStage)),
  },
  {
    label: 'Devices',
    numeric: true,
    sortValue: (_, summary) => summary?.deviceCount,
    defaultDirection: 'descending',
  },
  {
    label: 'Print volume',
    numeric: true,
    sortValue: (_, summary) => summary?.printVolume,
    defaultDirection: 'descending',
  },
  { label: 'Trend', sortValue: (_, summary) => summary?.printVolumeChange },
  { label: 'Renewal risk', sortValue: (_, summary) => riskRank(summary?.risk), defaultDirection: 'descending' },
  {
    label: 'Estimated fleet value',
    numeric: true,
    sortValue: (_, summary) => summary?.estimatedFleetValue,
    defaultDirection: 'descending',
  },
  { label: 'Last updated', sortValue: (_, summary) => (summary ? Date.parse(summary.generatedAt) : undefined) },
]

// Missing values sort below every real value, including zero.
const compareSortValues = (a: SortValue, b: SortValue, direction: SortDirection): number => {
  let result: number
  if (a == null || b == null) result = (a == null ? -1 : 0) - (b == null ? -1 : 0)
  else if (typeof a === 'string' && typeof b === 'string') result = a.localeCompare(b)
  else result = Number(a) - Number(b)
  return direction === 'ascending' ? result : -result
}

const ProductLogo: FunctionComponent<{ product?: string }> = ({ product }) => {
  const normalizedProduct = product?.trim().toLowerCase()

  if (normalizedProduct === Product.Hive?.toLowerCase() || normalizedProduct === 'hive') {
    return (
      <svg width='20' height='18' viewBox='0 0 409 360' fill='none' className={styles.productLogo} aria-hidden='true'>
        <path
          d='M294.2 360C290.1 360 286 359 282.2 356.8C270.7 350.2 266.8 335.5 273.4 324L356.5 180L338.3 148.5C331.7 137 335.6 122.3 347.1 115.7C358.6 109.1 373.3 113 379.9 124.5L405 168C409.3 175.4 409.3 184.6 405 192L315 348C310.5 355.7 302.5 360 294.2 360Z'
          fill='#8EC741'
        />
        <path
          d='M342 58.7L315 12C310.7 4.6 302.8 0 294.2 0H240.3C229.4 0 219.9 7.3 217.1 17.8C214.3 28.3 218.9 39.4 228.3 44.8L309.2 91.5C312.9 93.7 317.1 94.7 321.2 94.7C327.4 94.7 333.6 92.3 338.2 87.7C345.8 80 347.4 68.1 342 58.7Z'
          fill='#33C2AF'
        />
        <path
          d='M294.2 360H114.1C105.5 360 97.6 355.4 93.3 348L3.20001 192C-1.09999 184.6 -1.09999 175.4 3.20001 168L93.3 12C97.6 4.6 105.5 0 114.1 0H164.3C177.6 0 188.3 10.7 188.3 24C188.3 37.3 177.6 48 164.3 48H128L51.7 180L127.9 312H294.2C307.5 312 318.2 322.7 318.2 336C318.2 349.3 307.5 360 294.2 360Z'
          fill='#27AA27'
        />
      </svg>
    )
  }

  if (normalizedProduct === Product.Pocket?.toLowerCase() || normalizedProduct === 'pocket') {
    return (
      <svg width='17' height='18' viewBox='0 0 336 360' fill='none' className={styles.productLogo} aria-hidden='true'>
        <path
          d='M263.1 7.4C261.2 2.9 256.8 0 252 0H84C79.2 0 74.8 2.9 72.9 7.4C71 11.9 72 17 75.4 20.4L159.4 106C161.7 108.3 164.7 109.6 168 109.6C171.3 109.6 174.3 108.3 176.6 106L260.6 20.4C263.9 17 264.9 11.8 263.1 7.4Z'
          fill='#33C2AF'
        />
        <path
          d='M240 319.9C231.7 319.9 223.7 315.6 219.2 308C212.5 296.6 216.4 281.9 227.8 275.2L287.9 240.1V24C287.9 10.7 298.6 0 311.9 0C325.2 0 335.9 10.7 335.9 24V247C335.9 259.8 329.1 271.7 318 278.1L252 316.6C248.3 318.8 244.1 319.9 240 319.9Z'
          fill='#8EC741'
        />
        <path
          d='M168 360C161.7 360 155.5 358.4 149.9 355.1L17.9 278.1C6.8 271.7 0 259.8 0 247V24C0 10.7 10.7 0 24 0C37.3 0 48 10.7 48 24V240.1L168 310.1L227.9 275.2C239.3 268.5 254 272.4 260.7 283.8C267.4 295.2 263.5 309.9 252.1 316.6L186.1 355.1C180.5 358.4 174.3 360 168 360Z'
          fill='#27AA27'
        />
      </svg>
    )
  }

  if (
    normalizedProduct === Product.NG?.toLowerCase() ||
    normalizedProduct === 'ng' ||
    normalizedProduct === 'papercut ng'
  ) {
    return (
      <svg width='18' height='18' viewBox='0 0 360 360' fill='none' className={styles.productLogo} aria-hidden='true'>
        <path
          d='M180 360C80.7 360 0 279.3 0 180C0 80.7 80.7 0 180 0C193.3 0 204 10.7 204 24C204 37.3 193.3 48 180 48C107.2 48 48 107.2 48 180C48 252.8 107.2 312 180 312C193.3 312 204 322.7 204 336C204 349.3 193.3 360 180 360Z'
          fill='#27AA27'
        />
        <path
          d='M180 360C166.7 360 156 349.3 156 336C156 322.7 166.7 312 180 312C218.6 312 255.2 295.1 280.4 265.7C300.8 241.8 312 211.4 312 180C312 166.7 322.7 156 336 156C349.3 156 360 166.7 360 180C360 222.9 344.7 264.4 316.8 296.9C282.6 337 232.7 360 180 360Z'
          fill='#8EC741'
        />
        <path
          d='M308.4 51.5C305.3 48.4 300.7 47.2 296.5 48.5L176.5 84.6C171.1 86.2 167.6 91.3 168 96.9C168.4 102.5 172.6 107.1 178.1 107.9L241.9 118.1L252.2 181.9C253.1 187.4 257.7 191.6 263.3 192C263.6 192 263.8 192 264.1 192C269.4 192 274.1 188.5 275.6 183.4L311.5 63.4C312.6 59.2 311.5 54.6 308.4 51.5Z'
          fill='#33C2AF'
        />
      </svg>
    )
  }

  // Default / PaperCut MF
  return (
    <svg width='18' height='18' viewBox='0 0 360 360' fill='none' className={styles.productLogo} aria-hidden='true'>
      <path
        d='M336 204C322.7 204 312 193.3 312 180V48H180C166.7 48 156 37.3 156 24C156 10.7 166.7 0 180 0H336C349.3 0 360 10.7 360 24V180C360 193.3 349.3 204 336 204Z'
        fill='#8EC741'
      />
      <path
        d='M180 360H24C10.7 360 0 349.3 0 336V24C0 10.7 10.7 0 24 0H180C193.3 0 204 10.7 204 24C204 37.3 193.3 48 180 48H48V312H180C193.3 312 204 322.7 204 336C204 349.3 193.3 360 180 360Z'
        fill='#27AA27'
      />
      <path
        d='M359.1 241.4C357.2 236.9 352.9 234 348 234H246C239.4 234 234 239.4 234 246V348C234 352.9 236.9 357.2 241.4 359.1C242.9 359.7 244.4 360 246 360C249.1 360 252.2 358.8 254.5 356.5L356.5 254.5C359.9 251.1 360.9 245.9 359.1 241.4Z'
        fill='#33C2AF'
      />
    </svg>
  )
}

const CustomerIntelligenceTable: FunctionComponent<Props> = ({
  teamID,
  hosts,
  summaries,
  summariesStatus = 'loading',
  searchFilter,
  onSearchChange,
  searchTerm,
  onUpdated,
}) => {
  const navigate = useNavigate()
  const canUpdate = AuthRole.hasPermission('editIntelligence')
  const [updating, setUpdating] = useState<ReadonlySet<string>>(new Set())

  // Update rebuilds an existing brief; Generate creates the first one, since rebuild
  // needs a brief to exist. A 409 means the server does not consider the brief stale,
  // or it is already being generated, so there is nothing to do.
  const runBriefAction = (host: HostData, action: BriefAction) => {
    const { path, done } = briefActions[action]
    setUpdating(current => new Set(current).add(host.id))
    axios
      .post(`/api/teams/${teamID}/customers/${host.id}/briefs/health-check/${path}`)
      .catch(err => {
        if (err?.response?.status !== 409) throw err
      })
      .then(() => {
        Toaster.notifySuccess(`${host.name} ${done}.`)
        onUpdated?.()
      })
      .catch(() => Toaster.notifyFailure(`${host.name} could not be ${done}.`))
      .finally(() =>
        setUpdating(current => {
          const next = new Set(current)
          next.delete(host.id)
          return next
        })
      )
  }

  const briefActionButton = (host: HostData, action: BriefAction) => {
    const { label, tip } = briefActions[action]
    return (
      <span className='has-tip'>
        <button
          type='button'
          className={`btn x-sml ${styles.updateButton}`}
          onClick={event => {
            event.stopPropagation()
            runBriefAction(host, action)
          }}
        >
          {label}
        </button>
        <span className='tip center'>
          {tip[0]}
          <span className='blk'>{tip[1]}</span>
        </span>
      </span>
    )
  }

  // The main sort (kept under its original keys), plus any further columns added with Shift-click
  const [sortLabel, setSortLabel] = useLocalStorageState('intelligence-sort-column', columns[0].label)
  const [sortDirection, setSortDirection] = useLocalStorageState<SortDirection>('intelligence-sort-dir', 'ascending')
  const [moreSorts, setMoreSorts] = useLocalStorageState<SortKey[]>('intelligence-sort-more', [])
  const sortColumn = columns.find(column => column.label === sortLabel && column.sortValue) ?? columns[0]
  const sortKeys: { column: Column; direction: SortDirection }[] = [{ column: sortColumn, direction: sortDirection }]
  moreSorts.forEach(key => {
    const column = columns.find(c => c.label === key.label && c.sortValue)
    if (column && !sortKeys.some(k => k.column === column)) sortKeys.push({ column, direction: key.direction })
  })

  // A click sorts by that column alone (again to reverse it). Shift-click adds it as a further sort, or, if it's
  // already one, reverses it and then takes it out.
  const toggleSort = (column: Column, addToSort: boolean) => {
    const defaultDirection = column.defaultDirection ?? 'ascending'
    const flip = (direction: SortDirection): SortDirection => (direction === 'ascending' ? 'descending' : 'ascending')
    if (!addToSort || (column === sortColumn && sortKeys.length === 1)) {
      setSortDirection(column === sortColumn ? flip(sortDirection) : defaultDirection)
      setSortLabel(column.label)
      setMoreSorts([])
      return
    }
    if (column === sortColumn) {
      setSortDirection(flip(sortDirection))
      return
    }
    const more = sortKeys.slice(1).map(k => ({ label: k.column.label, direction: k.direction }))
    const existing = more.find(k => k.label === column.label)
    if (!existing) setMoreSorts([...more, { label: column.label, direction: defaultDirection }])
    else if (existing.direction === defaultDirection)
      setMoreSorts(more.map(k => (k === existing ? { ...k, direction: flip(k.direction) } : k)))
    else setMoreSorts(more.filter(k => k !== existing))
  }

  const summaryFor = (host: HostData) => (isHealthCheckNotApplicable(host) ? undefined : summaries?.get(host.id))

  // Each sort in turn breaks the previous one's ties; any left fall back to customer name, in the main direction.
  const sortedHosts = [...hosts].sort((a, b) => {
    let result = 0
    sortKeys.some(({ column, direction }) => {
      result = compareSortValues(column.sortValue?.(a, summaryFor(a)), column.sortValue?.(b, summaryFor(b)), direction)
      return result !== 0
    })
    return result || compareSortValues(a.name, b.name, sortDirection)
  })

  return (
    <div className={styles.customerTableContainer}>
      <div className={styles.tableHeader}>
        <h2 className={styles.tableHeaderTitle}>Customers</h2>
        {onSearchChange && (
          <div className={`search ${styles.tableHeaderSearch}`}>
            <input
              type='search'
              placeholder='Search by name'
              size={15}
              value={searchFilter || ''}
              onChange={e => onSearchChange(e.target.value)}
            />
          </div>
        )}
      </div>

      {sortedHosts.length === 0 ? (
        <div className='empty padded'>{searchTerm ? 'No matching customers found.' : 'No customers yet.'}</div>
      ) : (
        <table className={styles.customerTable} aria-label='Customer intelligence'>
          <thead>
            <tr>
              {columns.map((column, columnIndex) => {
                const rank = sortKeys.findIndex(k => k.column === column)
                const active = rank === 0
                const sorted = sortKeys[rank]
                const sortButtonClassName = [
                  styles.sortButton,
                  sorted && (sorted.direction === 'ascending' ? styles.sortAscending : styles.sortDescending),
                ]
                  .filter(Boolean)
                  .join(' ')
                return (
                  <th
                    key={column.label}
                    scope='col'
                    className={column.numeric ? styles.numericColumn : undefined}
                    aria-sort={active ? sortDirection : undefined}
                  >
                    {column.sortValue ? (
                      <span className='has-tip'>
                        <button
                          type='button'
                          className={sortButtonClassName}
                          onClick={e => toggleSort(column, e.shiftKey)}
                        >
                          {column.label}
                          {/* with more than one sort, each sorted column shows its place in the order */}
                          {sortKeys.length > 1 && sorted && (
                            <span className={styles.sortRank} aria-label={`sort ${rank + 1}`}>
                              {rank + 1}
                            </span>
                          )}
                        </button>
                        {/* the standard tip, as on the Briefs tab: how to sort by more than one column */}
                        <span
                          // the first and last columns' tips open inwards, so they stay within the table
                          className={`tip ${
                            columnIndex === 0
                              ? styles.sortTipStart
                              : columnIndex === columns.length - 1
                              ? styles.sortTipEnd
                              : 'center'
                          } ${styles.sortTip}`}
                          aria-hidden='true'
                        >
                          Shift-click to sort by more than one column
                        </span>
                      </span>
                    ) : (
                      column.label
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {sortedHosts.map(host => {
              const notApplicable = isHealthCheckNotApplicable(host)
              const summary = summaryFor(host)
              const loading = !notApplicable && !summary && summariesStatus === 'loading'
              const isUpdating = updating.has(host.id)

              // While summaries load, or a customer updates, placeholder bars stand in for the numbers.
              const placeholder = !notApplicable && (loading || isUpdating)
              const cell = (content: React.ReactNode, numeric = false) => {
                const className = numeric ? styles.numericColumn : undefined
                if (placeholder) {
                  // while updating, the old value stays in place (invisible) under the bar, so the
                  // column keeps its width and nothing shifts
                  return (
                    <td className={`${className ?? ''} ${styles.placeholderCell}`} aria-hidden='true'>
                      {isUpdating && <span className={styles.holdContent}>{content}</span>}
                      <span className={`${styles.skeleton} skeleton`} />
                    </td>
                  )
                }
                return <td className={className}>{notApplicable ? NOT_APPLICABLE : content}</td>
              }

              return (
                <tr
                  key={host.id}
                  className={styles.customerTableRow}
                  aria-busy={placeholder || undefined}
                  onClick={() => navigate(`/intelligence-hub/${host.id}`)}
                >
                  <td>
                    <div className={styles.customerNameWrapper}>
                      <Link
                        to={`/intelligence-hub/${host.id}`}
                        className={styles.customerNameLink}
                        onClick={event => event.stopPropagation()}
                      >
                        <ProductLogo product={host.product} />
                        {host.name}
                      </Link>
                      {isUpdating && <span className={styles.srOnly}>Updating</span>}
                    </div>
                  </td>
                  {cell(formatLifecycleStage(stageOrDefault(summary?.lifecycleStage)))}
                  {cell(formatOrEmpty(summary?.deviceCount, formatCount), true)}
                  {cell(formatOrEmpty(summary?.printVolume, formatCount), true)}
                  {cell(formatOrEmpty(summary?.printVolumeChange, formatTrend))}
                  {cell(formatOrEmpty(summary?.risk, formatRisk))}
                  {cell(formatOrEmpty(summary?.estimatedFleetValue, formatFleetValue(summary?.currencyCode)), true)}
                  {cell(
                    summary ? (
                      <span className={styles.lastUpdated}>
                        <Moment element='span' format='D MMM YYYY' date={summary.generatedAt} />
                        {isStale(summary.generatedAt) &&
                          (!canUpdate ? (
                            <span className={styles.staleBadge}>Stale</span>
                          ) : (
                            briefActionButton(host, 'update')
                          ))}
                      </span>
                    ) : summariesStatus === 'loaded' ? (
                      <span className={styles.lastUpdated}>
                        {NO_DATA_YET}
                        {canUpdate && briefActionButton(host, 'generate')}
                      </span>
                    ) : (
                      EMPTY_VALUE
                    )
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

export { CustomerIntelligenceTable }
